import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollReveal from '../../components/ui/ScrollReveal';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const includes = [
  'Fully bespoke programme built around your lifestyle and goals',
  'Technique coaching and movement correction in every session',
  'Structured progressive overload with measurable benchmarks',
  'Practical nutrition guidance aligned to your training phase',
  'Direct accountability and support between sessions',
  'Regular progress reviews and programme evolution',
];

const steps = [
  {
    number: '01',
    title: 'Free Consultation',
    body: 'We discuss your goals, schedule, training history, and barriers to create a clear transformation plan.',
  },
  {
    number: '02',
    title: 'Bespoke Programme Built',
    body: 'Laura designs your personal plan with tailored sessions, progression targets, and coaching checkpoints.',
  },
  {
    number: '03',
    title: 'Train & Transform',
    body: 'You train with expert guidance, stay accountable, and build visible strength, confidence, and momentum.',
  },
];

const forYou = [
  'You want 1-2-1 personal training London professionals trust',
  'You value expert form coaching and safe strength progression',
  'You have specific body composition or performance goals',
  'You want consistent accountability from a premium coach',
  'You want a personal trainer London clients recommend',
];

const expectations = [
  'A tailored programme that evolves with your progress',
  'Direct feedback, coaching cues, and technical precision',
  'Regular progress reviews and honest accountability',
  'A high-performance environment without intimidation',
  'A long-term strategy focused on sustainable results',
];

export default function PersonalTraining() {
  useSEO({
    title: '1-2-1 Personal Training London | Laura Martin Fitness',
    description:
      'Elite one-to-one personal training in London with Laura Martin. Bespoke programmes, technique coaching & guaranteed results. Book your free consult.',
  });

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Header />
      <main>
        <section className="relative h-[50vh] min-h-[400px] lg:h-[70vh] lg:min-h-[560px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1800&q=80"
              alt="1-2-1 personal training London gym session"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1c1917]/80 via-[#1c1917]/45 to-transparent" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #faf8f5 0%, rgba(250,248,245,0.35) 18%, transparent 42%)' }} />
          </div>
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-36">
            <div className="absolute bottom-0 pb-10 lg:pb-24 max-w-3xl">
              <span className="inline-block px-4 py-1.5 bg-[#c9a96e]/20 border border-[#c9a96e]/40 text-[#c9a96e] text-[9px] font-sans font-semibold tracking-[0.3em] uppercase rounded-full mb-5">
                In-Person · London
              </span>
              <h1 className="text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.6rem, 5.5vw, 4.5rem)', fontWeight: 600, lineHeight: 1.0 }}>
                1-2-1 Personal
                <br />
                <em className="text-[#c9a96e] not-italic">Training London</em>
              </h1>
              <p className="text-white/70 font-sans text-base max-w-xl">
                Elite one-to-one coaching for clients who want high-level support, expert structure, and visible long-term change.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#faf8f5] py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16 items-start">
            <ScrollReveal>
              <div>
                <h2 className="text-stone-900 text-4xl mb-6 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Bespoke Coaching, Built Around You
                </h2>
                <p className="text-stone-600 font-sans text-base leading-relaxed mb-5">
                  If you are looking for 1-2-1 personal training London clients choose for quality and consistency, Laura delivers a fully personalised coaching experience from day one.
                </p>
                <p className="text-stone-600 font-sans text-base leading-relaxed mb-5">
                  Every session is built for your body, your goals, and your training history. You receive expert technical guidance, clear progression, and the accountability needed to keep momentum high.
                </p>
                <p className="text-stone-600 font-sans text-base leading-relaxed">
                  As a personal trainer London professionals trust, Laura creates high-performance programmes that develop strength, confidence, and long-term physical resilience.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={120}>
              <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm">
                <h3 className="text-stone-900 text-2xl mb-6 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {includes.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm leading-relaxed">
                      <span className="w-5 h-5 rounded-full bg-[#c9a96e]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#c9a96e] text-[10px]" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section className="bg-stone-50 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <ScrollReveal className="mb-12">
              <h2 className="text-stone-900 text-4xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                How It Works
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {steps.map((step, index) => (
                <ScrollReveal key={step.title} delay={index * 80}>
                  <div className="h-full bg-white rounded-2xl border border-stone-200 p-8">
                    <span className="text-6xl leading-none text-[#c9a96e]/20 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {step.number}
                    </span>
                    <h3 className="text-stone-900 text-xl mt-4 mb-3 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {step.title}
                    </h3>
                    <p className="text-stone-600 font-sans text-sm leading-relaxed">{step.body}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-stone-50 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <ScrollReveal className="mb-8">
              <h2 className="text-stone-900 text-4xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Is This Right For You?
              </h2>
            </ScrollReveal>
            <div className="bg-white border border-stone-200 rounded-2xl p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div>
                <h3 className="text-stone-900 text-2xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  This Is For You If...
                </h3>
                <ul className="space-y-3">
                  {forYou.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm">
                      <i className="ri-checkbox-circle-line text-[#c9a96e] mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-stone-900 text-2xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  What To Expect...
                </h3>
                <ul className="space-y-3">
                  {expectations.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm">
                      <i className="ri-checkbox-circle-line text-[#c9a96e] mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-stone-100 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <ScrollReveal>
              <h2 className="text-stone-900 text-5xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Ready To Train At The Highest Level?
              </h2>
              <p className="text-stone-500 font-sans text-base max-w-2xl mx-auto mb-10">
                Start with a free consultation and receive a clear roadmap for your personal transformation.
              </p>
              <Link
                to="/contact"
                className="inline-block px-9 py-4 bg-[#c9a96e] text-white text-[10px] font-sans font-bold tracking-[0.28em] uppercase rounded-full hover:bg-[#a8863f] transition-all duration-300"
              >
                Book Free Consultation
              </Link>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
